import type {
  AuthResponse,
  CompleteRegistrationBody,
  EmailRequestBody,
  EmailVerifyBody,
  OtpRequestResult,
  PhoneRequestBody,
  PhoneVerifyBody,
  RequestDto,
  TelegramRequestBody,
  TelegramRequestResult,
  TelegramVerifyBody,
  UpdateProfileBody,
  UpdateClientNotificationsBody,
  AccountDeletionStateDto,
  UserDto,
} from '@lex/contracts';
import type { ApiClient } from './client';

export function createAuthApi(client: ApiClient) {
  return {
    requestPhoneCode(body: PhoneRequestBody): Promise<OtpRequestResult> {
      return client.post<OtpRequestResult>('/api/v1/auth/phone/request-code', body, {
        auth: false,
      });
    },
    verifyPhoneCode(body: PhoneVerifyBody): Promise<AuthResponse> {
      return client.post<AuthResponse>('/api/v1/auth/phone/verify-code', body, { auth: false });
    },
    requestEmailCode(body: EmailRequestBody): Promise<OtpRequestResult> {
      return client.post<OtpRequestResult>('/api/v1/auth/email/request-code', body, {
        auth: false,
      });
    },
    verifyEmailCode(body: EmailVerifyBody): Promise<AuthResponse> {
      return client.post<AuthResponse>('/api/v1/auth/email/verify-code', body, { auth: false });
    },
    requestTelegramCode(body: TelegramRequestBody = {}): Promise<TelegramRequestResult> {
      return client.post<TelegramRequestResult>('/api/v1/auth/telegram/request-code', body, {
        auth: false,
      });
    },
    verifyTelegramCode(body: TelegramVerifyBody): Promise<AuthResponse> {
      return client.post<AuthResponse>('/api/v1/auth/telegram/verify-code', body, { auth: false });
    },
    refresh(refreshToken: string): Promise<AuthResponse> {
      return client.post<AuthResponse>('/api/v1/auth/refresh', { refreshToken }, { auth: false });
    },
    logout(refreshToken?: string): Promise<{ ok: true }> {
      return client.post<{ ok: true }>('/api/v1/auth/logout', refreshToken ? { refreshToken } : {});
    },
    me(): Promise<UserDto> {
      return client.get<UserDto>('/api/v1/auth/me');
    },
    updateProfile(body: UpdateProfileBody): Promise<UserDto> {
      return client.patch<UserDto>('/api/v1/auth/me', body);
    },
    updateNotifications(body: UpdateClientNotificationsBody): Promise<UserDto> {
      return client.patch<UserDto>('/api/v1/auth/me/notifications', body);
    },
    requestPhoneChange(phone: string): Promise<OtpRequestResult> {
      return client.post('/api/v1/auth/me/phone/request', { phone });
    },
    confirmPhoneChange(phone: string, code: string): Promise<UserDto> {
      return client.post('/api/v1/auth/me/phone/confirm', { phone, code });
    },
    requestEmailChange(email: string): Promise<OtpRequestResult> {
      return client.post('/api/v1/auth/me/email/request', { email });
    },
    confirmEmailChange(email: string, code: string): Promise<UserDto> {
      return client.post('/api/v1/auth/me/email/confirm', { email, code });
    },
    deletionState(): Promise<AccountDeletionStateDto> {
      return client.get('/api/v1/auth/me/deletion');
    },
    requestDeletion(): Promise<{ scheduledAt: string }> {
      return client.post('/api/v1/auth/me/deletion', {});
    },
    cancelDeletion(): Promise<{ ok: true }> {
      return client.delete('/api/v1/auth/me/deletion');
    },
    completeRegistration(body: CompleteRegistrationBody): Promise<UserDto> {
      return client.post<UserDto>('/api/v1/auth/me/complete-registration', body);
    },
    myRequests(): Promise<RequestDto[]> {
      return client.get<RequestDto[]>('/api/v1/auth/me/requests');
    },
  };
}
